import { useEffect, useMemo, useState } from "react";
import { Box, Table, Thead, Tbody, Tr, Th, Td, Text, Spinner, Tabs, TabList, Tab, Flex } from "@chakra-ui/react";
import { unzipSync } from "fflate";

const MAX_ROWS = 5000;

type Sheet = { name: string; rows: string[][]; total: number };

// exceljs is large; it loads only when a spreadsheet is actually previewed.
async function readWorkbook(bytes: Uint8Array): Promise<Sheet[]> {
  const ExcelJS = (await import("exceljs")).default;
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer);
  return wb.worksheets.map((ws) => {
    const rows: string[][] = [];
    ws.eachRow({ includeEmpty: false }, (row) => {
      if (rows.length >= MAX_ROWS) return;
      const cells: string[] = [];
      for (let c = 1; c <= ws.columnCount; c++) cells.push(cellText(row.getCell(c).value));
      rows.push(cells);
    });
    return { name: ws.name, rows, total: ws.actualRowCount };
  });
}

function cellText(v: unknown): string {
  if (v == null) return "";
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  if (typeof v !== "object") return String(v);
  const o = v as Record<string, unknown>;
  if ("result" in o) return cellText(o.result);                       // formula: show its value
  if (Array.isArray(o.richText)) return o.richText.map((t: { text: string }) => t.text).join("");
  if ("text" in o) return cellText(o.text);                           // hyperlink
  if ("error" in o) return String(o.error);
  return "";
}

export function SheetPreview({ bytes }: { bytes: Uint8Array }) {
  const [sheets, setSheets] = useState<Sheet[]>();
  const [error, setError] = useState<string>();
  const [active, setActive] = useState(0);

  useEffect(() => {
    let cancelled = false;
    readWorkbook(bytes)
      .then((s) => !cancelled && setSheets(s))
      .catch((e) => { console.error("Spreadsheet preview failed", e); if (!cancelled) setError("This spreadsheet could not be read (password protected or damaged?)."); });
    return () => { cancelled = true; };
  }, [bytes]);

  if (error) return <Text p={6} color="red.500">{error}</Text>;
  if (!sheets) return <Flex h="100%" align="center" justify="center"><Spinner size="lg" /></Flex>;
  const sheet = sheets[active];
  return (
    <Flex direction="column" h="100%">
      <Box flex="1" minH={0} overflow="auto">
        {sheet.total > MAX_ROWS && (
          <Text p={2} fontSize="sm" color="orange.600">Showing the first {MAX_ROWS.toLocaleString()} of {sheet.total.toLocaleString()} rows. Download the file for the rest.</Text>
        )}
        <Table size="sm">
          <Tbody>
            {sheet.rows.map((r, i) => (
              <Tr key={i}><Td color="gray.500" position="sticky" left={0} bg="gray.50">{i + 1}</Td>{r.map((c, j) => <Td key={j} whiteSpace="nowrap">{c}</Td>)}</Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
      <Tabs index={active} onChange={setActive} size="sm" borderTop="1px solid" borderColor="gray.200">
        <TabList overflowX="auto">{sheets.map((s) => <Tab key={s.name} whiteSpace="nowrap">{s.name}</Tab>)}</TabList>
      </Tabs>
    </Flex>
  );
}

const fmtSize = (n: number) => (n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1048576).toFixed(1)} MB`);

export function ZipPreview({ bytes }: { bytes: Uint8Array }) {
  const entries = useMemo(() => {
    try {
      return Object.entries(unzipSync(bytes, { filter: () => true })).map(([name, data]) => ({ name, size: data.length }));
    } catch {
      return null;
    }
  }, [bytes]);
  if (!entries) return <Text p={6} color="red.500">This archive could not be read.</Text>;
  return (
    <Box h="100%" overflow="auto">
      <Text p={2} fontSize="sm" color="gray.600">{entries.length} entries</Text>
      <Table size="sm">
        <Thead><Tr><Th>Name</Th><Th isNumeric>Size</Th></Tr></Thead>
        <Tbody>{entries.map((e) => <Tr key={e.name}><Td>{e.name}</Td><Td isNumeric>{fmtSize(e.size)}</Td></Tr>)}</Tbody>
      </Table>
    </Box>
  );
}
